import React, { useMemo, useState } from "react";
import { Box, Drawer, Typography, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import ShellContext, { useShellState } from "./ShellContext";
import SideNav from "./SideNav";
import TopBar from "./TopBar";
import MobileBottomNav from "./MobileBottomNav";
import BrandLogo from "./BrandLogo";
import { getNavItems, findActiveItem, titleFromPath } from "../../config/navigation";
import { can, canAll, getPortal, getStoredUser } from "../../utils/auth";
import { layout } from "../../theme/tokens";

const STORAGE_KEY = "sidebar-collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * The application frame.
 *
 * Desktop (>= md): fixed collapsible sidebar + top bar.
 * Mobile  (<  md): native-style top bar + bottom tab bar, no sidebar.
 */
export default function AppShell({ children }) {
  const isMobile = useMediaQuery((t) => t.breakpoints.down(layout.mobileBreakpoint));
  const { pathname } = useLocation();
  const shell = useShellState();

  const [collapsed, setCollapsed] = useState(readCollapsed);

  const portal = getPortal();
  const items = useMemo(
    () => getNavItems(portal, (keys) => can(keys), (keys) => canAll(keys)),
    [portal]
  );
  const activeItem = findActiveItem(items, pathname);

  const toggleSidebar = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        // Non-fatal: the preference just won't survive a reload.
      }
      return next;
    });
  };

  // Every role now resolves to a portal, so there is no "no role" case left
  // to catch — only a missing session, which means the stored user was
  // cleared out from under the app.
  if (!getStoredUser()) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          Not signed in
        </Typography>
      </Box>
    );
  }

  const sidebarWidth = collapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth;
  const title =
    shell.pageMeta.title || activeItem?.label || titleFromPath(pathname) || "Geopro";

  // A route deeper than its nav item (or matching none at all) is a detail
  // screen, and on a phone that's a dead end without a back affordance. Screens
  // can still override the target by passing `backTo` through PageHeader.
  const isDetailRoute = activeItem ? pathname !== activeItem.to : true;
  const backTo = shell.pageMeta.backTo ?? (isDetailRoute ? true : undefined);

  return (
    <ShellContext.Provider value={{ ...shell, isMobile }}>
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
              width: sidebarWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: sidebarWidth,
                boxSizing: "border-box",
                bgcolor: "nav.bg",
                color: "nav.fg",
                borderRight: 0,
                overflowX: "hidden",
                transition: (t) =>
                  t.transitions.create("width", {
                    duration: t.transitions.duration.shorter,
                  }),
              },
            }}
          >
            {/* Brand block, aligned to the top bar height. */}
            <Box
              sx={{
                height: layout.topBarHeight,
                display: "flex",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "flex-start",
                px: collapsed ? 0 : 2.5,
                borderBottom: 1,
                borderColor: "nav.divider",
                flexShrink: 0,
              }}
            >
              <BrandLogo height={collapsed ? 22 : 26} color="nav.fg" />
            </Box>
            <SideNav items={items} collapsed={collapsed} />
          </Drawer>
        )}

        <TopBar
          isMobile={isMobile}
          sidebarCollapsed={collapsed}
          onToggleSidebar={toggleSidebar}
          title={title}
          backTo={backTo}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            // Clear the fixed top bar (plus the notch on mobile).
            pt: isMobile
              ? `calc(${layout.topBarHeight}px + env(safe-area-inset-top))`
              : `${layout.topBarHeight}px`,
            // Clear the bottom tab bar (plus the home indicator).
            pb: isMobile
              ? `calc(${layout.bottomNavHeight}px + env(safe-area-inset-bottom))`
              : 0,
          }}
        >
          <Box
            key={pathname}
            sx={{
              flexGrow: 1,
              width: "100%",
              maxWidth: layout.contentMaxWidth,
              mx: "auto",
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 1.5, md: 2 },
              // Keyed on pathname so each navigation replays the entrance;
              // without the key React reuses the node and nothing animates.
              animation: "shellFadeIn .22s ease both",
              // Must stay non-scrolling: a scrollable ancestor makes recharts'
              // ResponsiveContainer measure the scroll width and grow without
              // bound. Wide tables scroll in their own TableContainer instead.
              minWidth: 0,
            }}
          >
            {children}
          </Box>
        </Box>

        {isMobile && <MobileBottomNav items={items} />}
      </Box>
    </ShellContext.Provider>
  );
}
